import { NumberInputField, NumberInputRoot } from "@/components/ui/number-input";
import { Stack, Heading, Flex, Grid, Fieldset, Text, Input, Progress, HStack } from "@chakra-ui/react";
import FormActions from "@/components/FormActions";
import { addDays, endOfYear, format, startOfWeek, startOfYear } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { Controller, useForm } from "react-hook-form";
import DateSelectController from "@/components/fields/DateSelect";
import { AmountInputController } from "@/components/fields/AmountInput";
import { BiAlarm, BiCalendarAlt } from "react-icons/bi";
import { Field } from "@/components/ui/field";
import SubmitButton from "@/components/buttons/SubmitButton";
import { deLocaleOption, formatIso8601, formatMinutes, parseIso, parseMinutes } from "@/data/DateTimeUtil";
import { InputGroup } from "@/components/ui/input-group";
import CancelButton from "@/components/buttons/CancelButton";
import { InfoTip } from "@/components/ui/toggle-tip";
import { useEffect } from "react";
import type { LocalRange } from "@/api/LocalRange";
import type { WorkModel } from "@/api/WorkModel";

export interface ContactFormData {
    interval: LocalRange;
    model: WorkModel;
}

export interface ContractFormProps {
    showInterval: boolean;
    submitDisabled?: boolean;
    submitCaption: string;
    editContract?: ContactFormData | null;
    onContractSubmit: (c: ContactFormData) => void | Promise<void>;
    cancelCaption: string;
    onContractCancel: () => void;
}

/**
 * Shows the working model setup.
 * @param showInterval tells to show start & end date to calculations, if is set to false then it show from when the model is in effect.
 * Later on could be used to set also until when us the model active -> e.g. by termination
 */
export const ContractForm = ({ showInterval, submitDisabled = false, submitCaption, editContract = null, onContractSubmit, cancelCaption, onContractCancel }: ContractFormProps) => {
    const today = new Date();
    const defaultContract: ContactFormData = {
        interval: {
            start: formatIso8601(startOfYear(today)),
            end: formatIso8601(endOfYear(today)),
        },
        model: {
            workPercentage: 100,
            selectedDays: [
                { day: 0, selected: true, percentage: 100 },
                { day: 1, selected: true, percentage: 100 },
                { day: 2, selected: true, percentage: 100 },
                { day: 3, selected: true, percentage: 100 },
                { day: 4, selected: true, percentage: 100 },
                // { day: 5, selected: false, percentage: 0 },
                // { day: 6, selected: false, percentage: 0 },
            ],
            minutesPerDay: 8 * 60,
            holidaysPerYear: 25,
        }
    };

    const startDate = startOfWeek(new Date(), deLocaleOption); // to show the days in the model
    const { control, watch, handleSubmit, reset, setValue, setError, formState: { isSubmitting, errors } } = useForm<ContactFormData>({
        defaultValues: defaultContract
    });

    // in edit or view mode fill in the form details
    useEffect(() => {
        if (editContract) {
            reset(editContract);
        }
    }, [editContract, reset]);

    const minutesPerDay = watch('model.minutesPerDay');

    const actualMinutesPerWeek = (): number => defaultContract.model.selectedDays.reduce((total, day, index) => {
        const value = watch(`model.selectedDays.${index}`);
        return total + (value.selected ? (minutesPerDay * value.percentage / 100) : 0);
    }, 0);

    const calculateWorkPercentage = (): number => {
        const actualMinutes = actualMinutesPerWeek();
        const minutesPerWeek = minutesPerDay * 5; // asuming 5 working days a week
        return Math.round(actualMinutes * 100 / minutesPerWeek);
    }

    const onCancelInternal = () => {
        // reset form and results components
        reset(defaultContract);
        onContractCancel();
    };

    // calculate based on intervals
    const onSubmitInternal = async (c: ContactFormData) => {
        if (submitDisabled) return;
        // validate interval only if is shown
        const startDate = parseIso(c.interval.start);
        const endDate = parseIso(c.interval.end);
        if (showInterval) {
            if (endDate < startDate) {
                setError('interval.end', { type: 'manual', message: 'Enddatum muss nach Startdatum liegen' });
                return;
            }
            // days beetwen the date can't be more than 3 years
            if (endDate.getFullYear() - startDate.getFullYear() > 3) {
                setError('interval.end', { type: 'manual', message: 'Dauer darf nicht mehr als 3 Jahre betragen' });
                return;
            }
        }
        // update payloed with iso dates
        c.interval.start = formatIso8601(startDate);
        c.interval.end = formatIso8601(endDate);
        console.info(`submitting ${JSON.stringify(c)}`);

        // with await will show the is submitting state
        await onContractSubmit(c);
    };

    return (
        <form noValidate onSubmit={handleSubmit(onSubmitInternal)}>
            <Fieldset.Root minW='0' invalid={!!errors.model?.selectedDays}>
                <Heading as='h2' size='lg'>{showInterval ? 'Einstellungen' : 'Anstellungsverhältnis'}</Heading>
                <Fieldset.ErrorText>
                    {errors.model?.selectedDays?.message}
                </Fieldset.ErrorText>

                <Grid templateColumns={showInterval ? 'repeat(2, minmax(0, 8rem))' : 'minmax(0, 8rem)'} gap={{ base: '2', sm: '4' }} mb='4'>
                    <DateSelectController w='full' control={control} required disabled={false} label={showInterval ? 'Anfangsdatum' : 'Gültig ab'} name='interval.start' clearable={false} />
                    {showInterval && <DateSelectController w='full' control={control} required disabled={false} label='Endatum' name='interval.end' clearable={false} />}
                </Grid>

                {/* Agreed Hours Per Day */}
                <Controller
                    control={control}
                    name='model.workPercentage'
                    render={({ field: { value } }) => (
                        <Progress.Root value={value} shape='full' colorPalette='blue' animated={true} variant='outline'>
                            <Progress.Label mb="2">
                                Arbeitsmodel
                                <InfoTip>Berechnet aus den Arbeitstagen</InfoTip>
                            </Progress.Label>
                            <HStack gap='.5rem'>
                                <Progress.Track flex="1">
                                    <Progress.Range />
                                </Progress.Track>
                                <Progress.ValueText w='3rem' textAlign='right' fontWeight='bold'>{value}%</Progress.ValueText>
                            </HStack>
                        </Progress.Root>
                    )}
                />

                <Flex textStyle='sm' fontWeight='normal' mb='1rem'>
                    <Text>Stunden Pro Woche</Text>
                    <Text pl='.5rem' fontWeight='bold'>{formatMinutes(actualMinutesPerWeek(), false)}</Text>
                </Flex>
                {/* <SeparatorText text='Konfigurieren' /> */}

                {/* first row */}
                <Stack direction={{ base: 'column', sm: 'row' }} gap='2rem'>
                    <Stack gap='1px'>

                        {/* Select Working Days */}
                        <Fieldset.Legend>Arbeitstage</Fieldset.Legend>
                        {defaultContract.model.selectedDays.map((day, index) => (
                            <Flex direction={'row'} gap='.9rem' align='center' key={`hours-c${index}`}>
                                <Flex w='11rem' key={`hours${index}`} align='left' justifyContent='space-between'>

                                    <Controller
                                        control={control}
                                        name={`model.selectedDays.${index}.selected`}
                                        defaultValue={day.selected}
                                        render={({ field: { onChange, value } }) => (
                                            <Checkbox
                                                checked={value}
                                                onChange={v => {
                                                    onChange(v);
                                                    const wp = calculateWorkPercentage();
                                                    setValue('model.workPercentage', wp);
                                                }}
                                                size='sm'
                                                colorPalette="blue"
                                                variant='outline'
                                                disabled={index > 4}
                                            >
                                                {format(addDays(startDate, day.day), 'EEEE', deLocaleOption)}
                                            </Checkbox>
                                        )}
                                    />

                                    <Controller
                                        control={control}
                                        name={`model.selectedDays.${index}.percentage`}
                                        defaultValue={day.percentage}
                                        render={({ field: { onChange, value } }) => (
                                            <NumberInputRoot
                                                value={(value).toString()}
                                                onValueChange={v => {
                                                    onChange(v.valueAsNumber * 100);
                                                    const wp = calculateWorkPercentage();
                                                    setValue('model.workPercentage', wp);
                                                }}
                                                step={0.05}
                                                w='4rem'
                                                size='xs'
                                                max={1}
                                                min={0.1}
                                                formatOptions={{
                                                    style: "percent",
                                                }}
                                                disabled={index > 4 || !watch(`model.selectedDays.${index}.selected`)}
                                            >
                                                <NumberInputField />
                                            </NumberInputRoot>
                                        )}
                                    />
                                </Flex>

                                <Controller
                                    control={control}
                                    name={`model.selectedDays.${index}.percentage`}
                                    //defaultValue={value}
                                    render={({ field: { value } }) => (
                                        <Text textStyle='sm' display={watch(`model.selectedDays.${index}.selected`) ? 'block' : 'none'}>
                                            {formatMinutes(minutesPerDay * value / 100)}
                                        </Text>
                                    )}
                                />
                            </Flex>
                        ))}
                    </Stack>


                    {/* second row */}
                    <Stack gap={4}>
                        <Controller
                            control={control}
                            name='model.minutesPerDay'
                            render={({ field: { value, onChange, name }, fieldState: { error, invalid } }) => (
                                <Field id={name} label='Stunden/Tag' required invalid={invalid} errorText={error?.message}>
                                    <InputGroup w='6rem' startElement={<BiAlarm color='blue' />}>
                                        <Input
                                            type="time"
                                            textAlign='right'
                                            css={{
                                                '&::-webkit-calendar-picker-indicator': {
                                                    display: 'none'
                                                }
                                            }}
                                            value={formatMinutes(value, true)}
                                            onChange={(e) => {
                                                const totalMinutes = parseMinutes(e.target.value);
                                                onChange(totalMinutes);
                                            }}
                                        />
                                    </InputGroup>
                                </Field>
                            )}
                        />

                        <AmountInputController w='6rem' control={control} name='model.holidaysPerYear' decimalPlaces={1} readOnly={false} label='Ferien/Jahr' icon={<BiCalendarAlt color='blue' />} />
                    </Stack>

                </Stack>

                <FormActions>
                    <CancelButton caption={cancelCaption} onClick={() => onCancelInternal()} />
                    <SubmitButton caption={submitCaption} isSubmitting={isSubmitting} disabled={submitDisabled || Object.keys(errors).length > 0} />
                </FormActions>

            </Fieldset.Root>
        </form>
    );
}
